import { useEffect } from 'react';
import {
  AUTH_TOKENS_UPDATED_EVENT,
  COGNITO_TOKENS_STORAGE_KEY,
  getMsUntilTokenRefresh,
  refreshAuthSession,
} from '../services/authService';

/** Tras un refresh fallido por red, o si el token sigue "por vencer", esperar esto. */
const MIN_RERUN_DELAY_MS = 30 * 1000;
const RETRY_AFTER_FAILURE_MS = 60 * 1000;
/** Tope de `setTimeout` (~24,8 días). */
const MAX_TIMEOUT_MS = 2147483647;

/**
 * Renovación proactiva del id token: agenda un refresh a `exp − 5 min` y
 * revisa al volver a la pestaña (`visibilitychange`, `focus`, `online`), porque
 * los timers se frenan en segundo plano y con la notebook suspendida.
 * También reagenda cuando cambian los tokens (refresh en esta u otra pestaña).
 *
 * Los servicios leen `cognito_tokens` en cada request, así que mantenerlo
 * fresco cubre también a los que todavía hacen `fetch` directo.
 *
 * @param {boolean} isLoggedIn
 * @see docs/auth/cognito-session.md
 */
export function useAuthTokenRefresh(isLoggedIn) {
  useEffect(() => {
    if (!isLoggedIn) return undefined;

    let disposed = false;
    let timerId = null;
    let lastRunAt = 0;

    const clearTimer = () => {
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    const setTimer = (fn, ms) => {
      clearTimer();
      timerId = setTimeout(fn, Math.min(Math.max(ms, 0), MAX_TIMEOUT_MS));
    };

    async function run() {
      clearTimer();
      lastRunAt = Date.now();
      const result = await refreshAuthSession();
      if (disposed) return;
      if (result.status === 'failed') {
        setTimer(run, RETRY_AFTER_FAILURE_MS);
        return;
      }
      schedule();
    }

    function schedule() {
      if (disposed) return;
      const ms = getMsUntilTokenRefresh();
      if (ms === null) {
        clearTimer();
        return;
      }
      if (ms > 0) {
        setTimer(run, ms);
        return;
      }
      // Por vencer: ahora, salvo que se haya intentado hace instantes (sin loops).
      const sinceLastRun = Date.now() - lastRunAt;
      if (sinceLastRun < MIN_RERUN_DELAY_MS) {
        setTimer(run, MIN_RERUN_DELAY_MS - sinceLastRun);
        return;
      }
      run();
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') schedule();
    };
    const onStorage = (event) => {
      if (event.key === null || event.key === COGNITO_TOKENS_STORAGE_KEY) schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', schedule);
    window.addEventListener('online', schedule);
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, schedule);

    return () => {
      disposed = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', schedule);
      window.removeEventListener('online', schedule);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_TOKENS_UPDATED_EVENT, schedule);
    };
  }, [isLoggedIn]);
}
